import CIcon from '@coreui/icons-react';
import { useMemo } from 'react';
import DataTable from 'components/common/DataTable';
import { omit, get } from 'lodash';
import { cibAdobeAcrobatReader } from '@coreui/icons';
import { formatCurrency, formatDate } from 'utils/helpers';
import Pagination from 'components/common/Pagination';
import type { IntlShape } from 'react-intl';
import type { TCustomerDataRecord, TCustomerDataRecords } from 'hooks/useCustomerDataAnalytics';
import requireValue from 'utils/requireValue';
import s from './CustomerDataAnalyticsTable.module.scss';

type TFormatMessage = IntlShape['formatMessage'];

/** A row of the table: the budget answers, the localized select values, the user and the last project. */
type TCustomerDataRow = Record<string, unknown>;

interface ICustomerDataAnalyticsTableProps {
  customerDataRecords: TCustomerDataRecords;
  changePage: (page: number) => void;
  currentPage: number;
  total: number | null;
  t: TFormatMessage;
}

const getTableHead = (t: TFormatMessage) => {
  return [
    { key: 'name', label: t({ id: 'customer_data_analytics.customer_name' }) },
    { key: 'email', label: t({ id: 'customer_data_analytics.customer_email' }) },
    { key: 'clientIP', label: t({ id: 'customer_data_analytics.ip_address' }) },
    {
      key: 'amount_with_loan_costs',
      label: t({ id: 'customer_data_analytics.amount_with_loan_costs' }),
    },
    { key: 'last_project_type', label: t({ id: 'customer_data_analytics.last_project_type' }) },
    {
      key: 'last_project_created_at',
      label: t({ id: 'customer_data_analytics.last_project_created_at' }),
    },
    { key: 'created_at', label: t({ id: 'customer_data_analytics.created_at' }) },
    { key: 'pdf', label: t({ id: 'customer_data_analytics.pdf' }) },
    { key: 'alone_or_partner', label: t({ id: 'customer_data_analytics.alone_or_partner' }) },
    { key: 'age', label: t({ id: 'customer_data_analytics.age' }) },
    { key: 'kids_quantity', label: t({ id: 'customer_data_analytics.kids_quantity' }) },
    { key: 'monthly_net_salary', label: t({ id: 'customer_data_analytics.monthly_net_salary' }) },
    {
      key: 'monthly_net_salary_partner',
      label: t({ id: 'customer_data_analytics.monthly_net_salary_partner' }),
    },
    { key: 'other_revenues', label: t({ id: 'customer_data_analytics.other_revenues' }) },
    { key: 'new_car', label: t({ id: 'customer_data_analytics.new_car' }) },
    { key: 'hobbies', label: t({ id: 'customer_data_analytics.hobbies' }) },
    {
      key: 'pension_and_insurances',
      label: t({ id: 'customer_data_analytics.pension_and_insurances' }),
    },
    { key: 'socializing', label: t({ id: 'customer_data_analytics.socializing' }) },
    { key: 'luxuries', label: t({ id: 'customer_data_analytics.luxuries' }) },
    { key: 'taking_a_break', label: t({ id: 'customer_data_analytics.taking_a_break' }) },
    { key: 'technology', label: t({ id: 'customer_data_analytics.technology' }) },
  ];
};

/** The PDF URL of a row, which is a string or missing (React leaves out a missing href). */
const pdfHref = (pdf: unknown): string | undefined => {
  if (typeof pdf === 'string') return pdf;
  return undefined;
};

const formatTableRecord = (
  tableRecord: TCustomerDataRecord,
  t: TFormatMessage,
): TCustomerDataRow => {
  const {
    user,
    amount_with_loan_costs,
    last_project_type,
    last_project_created_at,
    budgetEstimation,
  } = tableRecord;
  const { formData, pdf, created_at } = budgetEstimation;
  // The answers by name (the select values are read by key).
  const answers: Record<string, unknown> = formData;
  const firstName = get(user, 'profile.firstName', '-');
  const lastName = get(user, 'profile.lastName', '-');
  const name = `${firstName} ${lastName}`;
  const email = get(user, 'emails[0].address', '-');
  const clientIP = get(user, 'meta.clientIP', '-');

  const selectValueKeys = [
    'hobbies',
    'pension_and_insurances',
    'socializing',
    'new_car',
    'luxuries',
    'taking_a_break',
    'technology',
  ];
  const noValues: Record<string, string> = {};
  const localizedValues = selectValueKeys.reduce(
    (acc, key) => ({
      ...acc,
      [key]: answers[key] ? t({ id: `select_values.${String(answers[key])}` }) : '-',
    }),
    noValues,
  );

  const userData = { name, email, clientIP, age: formData.age };
  const budgetData = {
    pdf,
    created_at: formatDate(created_at),
    ...omit(formData, selectValueKeys),
    ...localizedValues,
  };

  const projectData = {
    amount_with_loan_costs: last_project_type ? formatCurrency(amount_with_loan_costs) : '-',
    last_project_type: last_project_type ? t({ id: `projects.types.${last_project_type}` }) : '-',
    last_project_created_at: last_project_created_at
      ? formatDate(new Date(last_project_created_at))
      : '-',
  };

  return { ...budgetData, ...userData, ...projectData };
};

const CustomerDataAnalyticsTable = ({
  customerDataRecords,
  changePage,
  currentPage,
  total,
  t,
}: ICustomerDataAnalyticsTableProps) => {
  const tableHead = useMemo(() => getTableHead(t), [t]);
  const items = useMemo(
    () =>
      // A missing list or item throws, as it did.
      requireValue(customerDataRecords, 'map').map((record) =>
        formatTableRecord(requireValue(record, 'user'), t),
      ),
    [customerDataRecords, t],
  );

  return (
    <>
      <DataTable
        addTableClasses={s.projectsTable}
        hover
        items={items}
        fields={tableHead}
        size="sm"
        scopedSlots={{
          pdf: ({ pdf }: TCustomerDataRow) => (
            <td>
              <a href={pdfHref(pdf)} target="_blank" rel="noopener noreferrer">
                <div className={s.pdfButton}>
                  <CIcon icon={cibAdobeAcrobatReader} />
                </div>
              </a>
            </td>
          ),
        }}
      />
      <Pagination {...{ changePage, currentPage, total }} />
    </>
  );
};

export default CustomerDataAnalyticsTable;
